/**
 * 🚀 Performance Context & Provider
 * 
 * Contexte global de performance pour gérer le chargement adaptatif
 * des composants basé sur la qualité réseau et les capacités du device
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import {
  getNetworkQualityService,
  NetworkMetrics,
  ConnectionQuality,
} from '../services/networkQuality';

// ============================================================================
// Types
// ============================================================================

export interface DeviceCapabilities {
  hardwareConcurrency: number;
  deviceMemory: number | null;
  isLowEndDevice: boolean;
  prefersReducedMotion: boolean;
  supportsWebGL: boolean;
  screenSize: 'small' | 'medium' | 'large';
}

export interface PerformanceConfig {
  // Images
  imageQuality: 'high' | 'medium' | 'low';
  imageLazyLoadOffset: string;
  enableImagePlaceholders: boolean;
  
  // Animations
  enableAnimations: boolean;
  animationDuration: number;
  enableTransitions: boolean;
  
  // Chargement
  maxConcurrentLoads: number;
  preloadCount: number;
  chunkSize: number;
  
  // UI
  enableSkeletons: boolean;
  enableBlur: boolean;
  enableShadows: boolean;
  
  // Vidéo
  videoAutoplay: boolean;
  videoPreload: 'none' | 'metadata' | 'auto';
  
  // Fonctionnalités avancées
  enableVirtualization: boolean;
  enableServiceWorker: boolean;
  cacheStrategy: 'aggressive' | 'moderate' | 'minimal';
}

export interface LoadingPhase {
  name: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
  priority: number;
  startTime?: number;
  endTime?: number;
}

export interface PerformanceContextValue {
  // État réseau
  networkMetrics: NetworkMetrics | null;
  connectionQuality: ConnectionQuality;
  isOnline: boolean;
  isSlowConnection: boolean;
  
  // Capacités device
  deviceCapabilities: DeviceCapabilities;
  
  // Configuration
  config: PerformanceConfig;
  updateConfig: (partial: Partial<PerformanceConfig>) => void;
  
  // Phases de chargement
  loadingPhases: LoadingPhase[];
  currentPhase: LoadingPhase | null;
  registerPhase: (name: string, priority?: number) => void;
  completePhase: (name: string) => void;
  errorPhase: (name: string) => void;
  
  // État global
  isInitialLoading: boolean;
  loadingProgress: number;
  
  // Utilitaires
  getOptimizedAnimationProps: () => any;
  shouldReduceMotion: () => boolean;
  shouldDeferLoad: (priority: 'critical' | 'high' | 'medium' | 'low' | 'lazy') => boolean;
  getImageQualitySuffix: () => string;
  
  // Métriques
  performanceScore: number;
  getPerformanceReport: () => PerformanceReport;
}

export interface PerformanceReport {
  timestamp: number;
  connectionQuality: ConnectionQuality;
  deviceScore: number;
  networkScore: number;
  overallScore: number;
  recommendations: string[];
  metrics: {
    ttfb?: number;
    fcp?: number;
    lcp?: number;
    cls?: number;
    fid?: number;
  };
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_CONFIG: PerformanceConfig = {
  imageQuality: 'high',
  imageLazyLoadOffset: '150px',
  enableImagePlaceholders: true,
  enableAnimations: true,
  animationDuration: 0.3,
  enableTransitions: true,
  maxConcurrentLoads: 4,
  preloadCount: 5,
  chunkSize: 15,
  enableSkeletons: true,
  enableBlur: true,
  enableShadows: true,
  videoAutoplay: true,
  videoPreload: 'metadata',
  enableVirtualization: false,
  enableServiceWorker: true,
  cacheStrategy: 'moderate',
};

// ============================================================================
// Context
// ============================================================================

const PerformanceContext = createContext<PerformanceContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface PerformanceProviderProps {
  children: ReactNode;
  initialConfig?: Partial<PerformanceConfig>;
}

export function PerformanceProvider({
  children,
  initialConfig = {},
}: PerformanceProviderProps) {
  // États
  const [networkMetrics, setNetworkMetrics] = useState<NetworkMetrics | null>(null);
  const [config, setConfig] = useState<PerformanceConfig>({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });
  const [loadingPhases, setLoadingPhases] = useState<LoadingPhase[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [deviceCapabilities, setDeviceCapabilities] = useState<DeviceCapabilities>({
    hardwareConcurrency: navigator.hardwareConcurrency || 4,
    deviceMemory: (navigator as any).deviceMemory || null,
    isLowEndDevice: false,
    prefersReducedMotion: false,
    supportsWebGL: false,
    screenSize: 'medium',
  });

  // Détecter les capacités du device
  useEffect(() => {
    const detectCapabilities = (): DeviceCapabilities => {
      const hardwareConcurrency = navigator.hardwareConcurrency || 4;
      const deviceMemory = (navigator as any).deviceMemory || null;
      
      // Détecter si c'est un device bas de gamme
      const isLowEndDevice = hardwareConcurrency <= 2 || 
        (deviceMemory !== null && deviceMemory <= 2);
      
      // Vérifier les préférences de mouvement réduit
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      
      // Vérifier le support WebGL
      let supportsWebGL = false;
      try {
        const canvas = document.createElement('canvas');
        supportsWebGL = !!(
          window.WebGLRenderingContext &&
          (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
        );
      } catch (e) {
        supportsWebGL = false;
      }
      
      // Taille d'écran
      const width = window.innerWidth;
      const screenSize: 'small' | 'medium' | 'large' = 
        width < 768 ? 'small' : width < 1280 ? 'medium' : 'large';

      return {
        hardwareConcurrency,
        deviceMemory,
        isLowEndDevice,
        prefersReducedMotion,
        supportsWebGL,
        screenSize,
      };
    };

    setDeviceCapabilities(detectCapabilities());

    // Écouter les changements de préférences
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setDeviceCapabilities(prev => ({
        ...prev,
        prefersReducedMotion: mediaQuery.matches,
      }));
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Souscrire au service de qualité réseau
  useEffect(() => {
    const networkService = getNetworkQualityService();
    
    const unsubscribe = networkService.subscribe((metrics) => {
      setNetworkMetrics(metrics);
      
      // Adapter la configuration basée sur le réseau
      const loadingConfig = networkService.getLoadingConfig();
      
      setConfig(prev => ({
        ...prev,
        imageQuality: loadingConfig.imageQuality,
        enableAnimations: loadingConfig.animationsEnabled,
        maxConcurrentLoads: loadingConfig.maxConcurrentLoads,
        preloadCount: loadingConfig.preloadCount,
        chunkSize: loadingConfig.chunkSize,
        enableSkeletons: loadingConfig.useSkeletons,
        videoAutoplay: loadingConfig.videoAutoplay,
        imageLazyLoadOffset: loadingConfig.lazyLoadOffset,
      }));
    });

    return () => unsubscribe();
  }, []);

  // Adapter aussi la configuration basée sur le device
  useEffect(() => {
    if (deviceCapabilities.isLowEndDevice || deviceCapabilities.prefersReducedMotion) {
      setConfig(prev => ({
        ...prev,
        enableAnimations: false,
        animationDuration: 0.1,
        enableBlur: false,
        enableShadows: deviceCapabilities.isLowEndDevice ? false : prev.enableShadows,
        maxConcurrentLoads: Math.min(prev.maxConcurrentLoads, 2),
      }));
    }
  }, [deviceCapabilities]);

  // Gérer la fin du chargement initial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Valeurs calculées
  const connectionQuality = networkMetrics?.quality || 'good';
  const isOnline = networkMetrics?.isOnline ?? true;
  const isSlowConnection = ['poor', 'moderate', 'offline'].includes(connectionQuality);

  // Calculer la progression du chargement
  const loadingProgress = useMemo(() => {
    if (loadingPhases.length === 0) return 100;
    const completed = loadingPhases.filter(p => p.status === 'complete').length;
    return Math.round((completed / loadingPhases.length) * 100);
  }, [loadingPhases]);

  // Phase actuelle
  const currentPhase = useMemo(() => {
    const loading = loadingPhases.find(p => p.status === 'loading');
    if (loading) return loading;
    
    const pending = loadingPhases
      .filter(p => p.status === 'pending')
      .sort((a, b) => a.priority - b.priority);
    
    return pending[0] || null;
  }, [loadingPhases]);

  // Score de performance
  const performanceScore = useMemo(() => {
    let score = 100;
    
    // Pénalité réseau
    if (connectionQuality === 'poor') score -= 40;
    else if (connectionQuality === 'moderate') score -= 20;
    else if (connectionQuality === 'offline') score -= 50;
    
    // Pénalité device
    if (deviceCapabilities.isLowEndDevice) score -= 20;
    
    // Bonus pour bon device et connexion
    if (connectionQuality === 'excellent' && !deviceCapabilities.isLowEndDevice) {
      score = Math.min(100, score + 10);
    }
    
    return Math.max(0, score);
  }, [connectionQuality, deviceCapabilities]);

  // Callbacks
  const updateConfig = useCallback((partial: Partial<PerformanceConfig>) => {
    setConfig(prev => ({ ...prev, ...partial }));
  }, []);

  const registerPhase = useCallback((name: string, priority: number = 50) => {
    setLoadingPhases(prev => {
      if (prev.some(p => p.name === name)) return prev;
      return [...prev, { name, status: 'pending', priority }];
    });
  }, []);

  const completePhase = useCallback((name: string) => {
    setLoadingPhases(prev =>
      prev.map(p =>
        p.name === name
          ? { ...p, status: 'complete', endTime: Date.now() }
          : p
      )
    );
  }, []);

  const errorPhase = useCallback((name: string) => {
    setLoadingPhases(prev =>
      prev.map(p =>
        p.name === name
          ? { ...p, status: 'error', endTime: Date.now() }
          : p
      )
    );
  }, []);

  // Utilitaires
  const getOptimizedAnimationProps = useCallback(() => {
    if (!config.enableAnimations || deviceCapabilities.prefersReducedMotion) {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0 },
      };
    }

    const duration = isSlowConnection
      ? config.animationDuration * 0.5
      : config.animationDuration;

    return {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -10 },
      transition: { duration, ease: 'easeOut' },
    };
  }, [config, deviceCapabilities.prefersReducedMotion, isSlowConnection]);

  const shouldReduceMotion = useCallback(() => {
    return !config.enableAnimations || 
           deviceCapabilities.prefersReducedMotion || 
           isSlowConnection;
  }, [config.enableAnimations, deviceCapabilities.prefersReducedMotion, isSlowConnection]);

  const shouldDeferLoad = useCallback((
    priority: 'critical' | 'high' | 'medium' | 'low' | 'lazy'
  ) => {
    if (priority === 'critical') return false;
    if (connectionQuality === 'offline') return true;
    if (priority === 'lazy') return true;
    
    if (isSlowConnection) {
      return priority !== 'high';
    }
    
    return priority === 'low';
  }, [connectionQuality, isSlowConnection]);

  const getImageQualitySuffix = useCallback(() => {
    switch (config.imageQuality) {
      case 'low': return '_thumb';
      case 'medium': return '_md';
      case 'high':
      default: return '';
    }
  }, [config.imageQuality]);

  const getPerformanceReport = useCallback((): PerformanceReport => {
    const recommendations: string[] = [];
    
    if (isSlowConnection) {
      recommendations.push('Connexion lente détectée - mode économie activé');
    }
    if (deviceCapabilities.isLowEndDevice) {
      recommendations.push('Device bas de gamme détecté - animations réduites');
    }
    if (!isOnline) {
      recommendations.push('Mode hors ligne - contenu mis en cache affiché');
    }

    // Essayer d'obtenir les métriques Web Vitals
    let metrics: PerformanceReport['metrics'] = {};
    try {
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navEntry) {
        metrics.ttfb = navEntry.responseStart - navEntry.requestStart;
      }
      
      const paintEntries = performance.getEntriesByType('paint');
      const fcpEntry = paintEntries.find(e => e.name === 'first-contentful-paint');
      if (fcpEntry) {
        metrics.fcp = fcpEntry.startTime;
      }
    } catch (e) {
      // Les métriques ne sont pas disponibles
    }

    return {
      timestamp: Date.now(),
      connectionQuality,
      deviceScore: deviceCapabilities.isLowEndDevice ? 40 : 80,
      networkScore: connectionQuality === 'excellent' ? 100 : 
                    connectionQuality === 'good' ? 80 :
                    connectionQuality === 'moderate' ? 50 : 20,
      overallScore: performanceScore,
      recommendations,
      metrics,
    };
  }, [connectionQuality, deviceCapabilities, isOnline, isSlowConnection, performanceScore]);

  // Valeur du contexte
  const value: PerformanceContextValue = {
    networkMetrics,
    connectionQuality,
    isOnline,
    isSlowConnection,
    deviceCapabilities,
    config,
    updateConfig,
    loadingPhases,
    currentPhase,
    registerPhase,
    completePhase,
    errorPhase,
    isInitialLoading,
    loadingProgress,
    getOptimizedAnimationProps,
    shouldReduceMotion,
    shouldDeferLoad,
    getImageQualitySuffix,
    performanceScore,
    getPerformanceReport,
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function usePerformance(): PerformanceContextValue {
  const context = useContext(PerformanceContext);
  
  if (context === undefined) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  
  return context;
}

// ============================================================================
// Utility Hook for Adaptive Config
// ============================================================================

export function useAdaptiveConfig<T extends keyof PerformanceConfig>(
  key: T
): PerformanceConfig[T] {
  const { config } = usePerformance();
  return config[key];
}

// ============================================================================
// HOC for Performance-Aware Components
// ============================================================================

export interface WithPerformanceProps {
  performanceConfig: PerformanceConfig;
  connectionQuality: ConnectionQuality;
  shouldReduceMotion: boolean;
}

export function withPerformance<P extends object>(
  WrappedComponent: React.ComponentType<P & WithPerformanceProps>
): React.FC<P> {
  return function PerformanceWrapper(props: P) {
    const { config, connectionQuality, shouldReduceMotion } = usePerformance();
    
    return (
      <WrappedComponent
        {...props}
        performanceConfig={config}
        connectionQuality={connectionQuality}
        shouldReduceMotion={shouldReduceMotion()}
      />
    );
  };
}

export default PerformanceProvider;
